import * as React from 'react';
import {
    DefaultButton,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
} from '@fluentui/react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiHelper } from '../../../ApiHelper';
import { apiFetch } from '../../../lib/api-client';
import {
    buildArchiveTree,
    findLastShutdownRecord,
    parseJsonLines,
    type ParsedJsonLine,
} from '../agencyLogs';
import type {
    AgencyLogsArchiveEntry,
    AgencyLogsArchiveIndexResult,
    AgencyLogsArchiveTreeNode,
} from '../types';
import {
    AuthenticatedDownloadLink,
    PageShell,
    buildAgencyLogsArchiveDownloadUrl,
    buildAgencyLogsArchiveFileUrl,
    buildAgencyLogsArchiveIndexUrl,
    getDownloadErrorMessage,
    useDocumentTitle,
} from '../shared';

const preStyle: React.CSSProperties = {
    margin: 0,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    fontFamily: 'Consolas, "Courier New", monospace',
    fontSize: 12,
};
const MAX_RENDERED_JSONL_RECORDS = 1000;
const SHUTDOWN_CONTEXT_RECORDS = 100;

export function AgencyLogsArchivePage() {
    useDocumentTitle('Agency logs archive');

    const navigate = useNavigate();
    const { serviceName, taskListId, taskId, workItemId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const ids = React.useMemo(
        () => parseArtifactIds(taskListId, taskId, workItemId),
        [taskId, taskListId, workItemId],
    );
    const [index, setIndex] = React.useState<AgencyLogsArchiveIndexResult | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const selectedPath = searchParams.get('path');
    const focus = searchParams.get('focus');

    const route = React.useMemo(() => serviceName && ids ? {
        serviceName,
        ...ids,
        archiveUrl: buildAgencyLogsArchiveDownloadUrl(serviceName, ids.taskListId, ids.taskId, ids.workItemId),
        indexUrl: buildAgencyLogsArchiveIndexUrl(serviceName, ids.taskListId, ids.taskId, ids.workItemId),
    } : undefined, [ids, serviceName]);

    React.useEffect(() => {
        if (!route) {
            setError('The Agency archive route is missing required identifiers.');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        ApiHelper<AgencyLogsArchiveIndexResult>(route.indexUrl)
            .then(result => setIndex({
                ...result,
                entries: Array.isArray(result.entries) ? result.entries : [],
            }))
            .catch(err => setError(err instanceof Error ? err.message : 'Failed to load the Agency logs archive.'))
            .finally(() => setLoading(false));
    }, [route]);

    const invalidSelectedPath = !!selectedPath
        && !!index
        && !index.entries.some(entry => entry.path === selectedPath);
    const selectedEntry = selectedPath
        ? index?.entries.find(entry => entry.path === selectedPath) ?? null
        : index?.entries[0] ?? null;
    const tree = React.useMemo(() => buildArchiveTree(index?.entries ?? []), [index]);

    React.useEffect(() => {
        if (selectedEntry && !selectedPath) {
            const next = new URLSearchParams(searchParams);
            next.set('path', selectedEntry.path);
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, selectedEntry, selectedPath, setSearchParams]);

    const eventsEntry = index?.entries.find(entry => entry.path === 'events.jsonl');

    return (
        <PageShell title="Agency logs archive">
            <Stack horizontal wrap tokens={{ childrenGap: 8 }} verticalAlign="center">
                <DefaultButton text="Back" onClick={() => navigate(-1)} />
                {route && (
                    <AuthenticatedDownloadLink
                        url={route.archiveUrl}
                        fallbackFileName={`workitem-${ids?.workItemId}-agency-logs.zip`}
                        fallbackErrorMessage="Failed to download the Agency logs archive."
                    >
                        Download ZIP
                    </AuthenticatedDownloadLink>
                )}
                <DefaultButton
                    text="Tokens"
                    disabled={!eventsEntry}
                    title={eventsEntry ? 'Open the final session.shutdown event' : 'events.jsonl is not present'}
                    onClick={() => {
                        if (!eventsEntry) return;
                        const next = new URLSearchParams(searchParams);
                        next.set('path', eventsEntry.path);
                        next.set('focus', 'session.shutdown');
                        setSearchParams(next, { replace: true });
                    }}
                />
            </Stack>

            {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
            {loading && <Spinner size={SpinnerSize.large} label="Loading archive contents..." />}
            {!loading && !error && index?.entries.length === 0 && (
                <MessageBar>The archive does not contain any files.</MessageBar>
            )}
            {!loading && !error && invalidSelectedPath && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    The requested file is not present in this archive. Select another file from the tree.
                </MessageBar>
            )}
            {!loading && !error && index?.warnings?.map(warning => (
                <MessageBar key={warning} messageBarType={MessageBarType.warning}>{warning}</MessageBar>
            ))}

            {!loading && !error && index && index.entries.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'stretch' }}>
                    <nav
                        aria-label="Archive files"
                        style={{
                            flex: '0 1 320px',
                            minWidth: 240,
                            maxHeight: '75vh',
                            overflow: 'auto',
                            border: '1px solid #e1dfdd',
                            borderRadius: 6,
                            padding: 12,
                            background: '#ffffff',
                        }}
                    >
                        <ArchiveTree
                            nodes={tree}
                            selectedPath={selectedEntry?.path}
                            onSelect={path => {
                                const next = new URLSearchParams(searchParams);
                                next.set('path', path);
                                next.delete('focus');
                                setSearchParams(next, { replace: true });
                            }}
                        />
                    </nav>
                    <main style={{ flex: '1 1 520px', minWidth: 0 }}>
                        {selectedEntry && route && (
                            <ArchiveFilePreview
                                entry={selectedEntry}
                                previewLimitBytes={index.previewLimitBytes}
                                previewUrl={buildAgencyLogsArchiveFileUrl(
                                    route.serviceName,
                                    route.taskListId,
                                    route.taskId,
                                    route.workItemId,
                                    selectedEntry.path,
                                )}
                                downloadUrl={buildAgencyLogsArchiveFileUrl(
                                    route.serviceName,
                                    route.taskListId,
                                    route.taskId,
                                    route.workItemId,
                                    selectedEntry.path,
                                    true,
                                )}
                                focusShutdown={focus === 'session.shutdown'}
                            />
                        )}
                    </main>
                </div>
            )}
        </PageShell>
    );
}

function ArchiveTree({
    nodes,
    selectedPath,
    onSelect,
}: {
    nodes: AgencyLogsArchiveTreeNode[];
    selectedPath?: string;
    onSelect: (path: string) => void;
}) {
    return (
        <ul style={{ listStyle: 'none', margin: 0, paddingLeft: 0 }}>
            {nodes.map(node => (
                <li key={`${node.isFile ? 'file' : 'directory'}-${node.path}`}>
                    {node.isFile ? (
                        <button
                            type="button"
                            onClick={() => onSelect(node.path)}
                            aria-current={selectedPath === node.path ? 'page' : undefined}
                            style={{
                                width: '100%',
                                border: 0,
                                padding: '4px 6px',
                                textAlign: 'left',
                                cursor: 'pointer',
                                background: selectedPath === node.path ? '#deecf9' : 'transparent',
                                fontWeight: selectedPath === node.path ? 600 : 400,
                            }}
                        >
                            {node.name}
                        </button>
                    ) : (
                        <ArchiveDirectoryNode node={node} selectedPath={selectedPath} onSelect={onSelect} />
                    )}
                </li>
            ))}
        </ul>
    );
}

function ArchiveDirectoryNode({
    node,
    selectedPath,
    onSelect,
}: {
    node: AgencyLogsArchiveTreeNode;
    selectedPath?: string;
    onSelect: (path: string) => void;
}) {
    const containsSelection = !!selectedPath?.startsWith(`${node.path}/`);
    const [open, setOpen] = React.useState(containsSelection);

    React.useEffect(() => {
        if (containsSelection) {
            setOpen(true);
        }
    }, [containsSelection]);

    return (
        <details open={open} onToggle={event => setOpen(event.currentTarget.open)}>
            <summary style={{ cursor: 'pointer', padding: '4px 6px', fontWeight: 600 }}>
                {node.name}
            </summary>
            <div style={{ paddingLeft: 14 }}>
                <ArchiveTree nodes={node.children} selectedPath={selectedPath} onSelect={onSelect} />
            </div>
        </details>
    );
}

function ArchiveFilePreview({
    entry,
    previewLimitBytes,
    previewUrl,
    downloadUrl,
    focusShutdown,
}: {
    entry: AgencyLogsArchiveEntry;
    previewLimitBytes: number;
    previewUrl: string;
    downloadUrl: string;
    focusShutdown: boolean;
}) {
    const [content, setContent] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const canPreview = entry.previewKind !== 'binary' && entry.length <= previewLimitBytes;

    React.useEffect(() => {
        setContent(null);
        setError(null);
        setLoading(false);
        if (!canPreview) {
            return;
        }

        setLoading(true);
        const controller = new AbortController();
        let cancelled = false;
        apiFetch(previewUrl, { signal: controller.signal })
            .then(async response => {
                if (!response.ok) {
                    throw new Error(await getDownloadErrorMessage(response, 'Failed to preview the archive file.'));
                }
                return response.text();
            })
            .then(value => {
                if (!cancelled) setContent(value);
            })
            .catch(err => {
                if (cancelled || (err instanceof DOMException && err.name === 'AbortError')) {
                    return;
                }
                setError(err instanceof Error ? err.message : 'Failed to preview the archive file.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [canPreview, previewUrl]);

    return (
        <Stack tokens={{ childrenGap: 12 }}>
            <Stack horizontal wrap horizontalAlign="space-between" verticalAlign="center" tokens={{ childrenGap: 8 }}>
                <div>
                    <Text variant="large"><strong>{entry.path}</strong></Text>
                    <div><Text variant="small">{formatBytes(entry.length)} uncompressed</Text></div>
                </div>
                <AuthenticatedDownloadLink
                    url={downloadUrl}
                    fallbackFileName={entry.name}
                    fallbackErrorMessage="Failed to download the archive file."
                >
                    Download file
                </AuthenticatedDownloadLink>
            </Stack>

            {!canPreview && (
                <MessageBar>
                    {focusShutdown
                        ? 'The final session.shutdown record cannot be located because events.jsonl is not previewable. Download the file to inspect it.'
                        : entry.previewKind === 'binary'
                        ? 'This file is not a recognized text format. Download it to inspect its contents.'
                        : `This file exceeds the ${formatBytes(previewLimitBytes)} preview limit. Download it to inspect its contents.`}
                </MessageBar>
            )}
            {loading && <Spinner size={SpinnerSize.medium} label="Loading file preview..." />}
            {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
            {content !== null && entry.previewKind === 'json' && <JsonPreview content={content} />}
            {content !== null && entry.previewKind === 'jsonl' && (
                <JsonLinesPreview content={content} focusShutdown={focusShutdown} />
            )}
            {content !== null && entry.previewKind === 'text' && (
                <pre style={preStyle}>{content}</pre>
            )}
        </Stack>
    );
}

function JsonPreview({ content }: { content: string }) {
    try {
        return <pre style={preStyle}>{JSON.stringify(JSON.parse(stripBom(content)), null, 2)}</pre>;
    } catch (error) {
        return (
            <Stack tokens={{ childrenGap: 8 }}>
                <MessageBar messageBarType={MessageBarType.warning}>
                    {error instanceof Error ? `Invalid JSON: ${error.message}` : 'Invalid JSON.'}
                </MessageBar>
                <pre style={preStyle}>{content}</pre>
            </Stack>
        );
    }
}

function JsonLinesPreview({ content, focusShutdown }: { content: string; focusShutdown: boolean }) {
    const lines = React.useMemo(() => parseJsonLines(stripBom(content)), [content]);
    const shutdownIndex = React.useMemo(() => findLastShutdownRecord(lines), [lines]);

    React.useEffect(() => {
        if (!focusShutdown || shutdownIndex < 0) return;
        document.getElementById(`agency-jsonl-record-${shutdownIndex}`)?.scrollIntoView?.({
            behavior: 'smooth',
            block: 'center',
        });
    }, [focusShutdown, shutdownIndex]);

    if (focusShutdown && shutdownIndex < 0) {
        return (
            <Stack tokens={{ childrenGap: 8 }}>
                <MessageBar messageBarType={MessageBarType.warning}>
                    No session.shutdown record was found in events.jsonl.
                </MessageBar>
                <JsonLineRecords lines={lines} shutdownIndex={shutdownIndex} />
            </Stack>
        );
    }

    return <JsonLineRecords lines={lines} shutdownIndex={focusShutdown ? shutdownIndex : -1} />;
}

function JsonLineRecords({ lines, shutdownIndex }: { lines: ParsedJsonLine[]; shutdownIndex: number }) {
    let startIndex = 0;
    let endIndex = Math.min(lines.length, MAX_RENDERED_JSONL_RECORDS);
    if (shutdownIndex >= 0) {
        startIndex = Math.max(0, shutdownIndex - SHUTDOWN_CONTEXT_RECORDS);
        endIndex = Math.min(lines.length, shutdownIndex + SHUTDOWN_CONTEXT_RECORDS + 1);
    }
    const visibleLines = lines.slice(startIndex, endIndex);

    return (
        <Stack tokens={{ childrenGap: 8 }}>
            {(startIndex > 0 || endIndex < lines.length) && (
                <MessageBar>
                    Showing records {startIndex + 1}-{endIndex} of {lines.length} to keep the preview responsive.
                </MessageBar>
            )}
            {visibleLines.map((line, visibleIndex) => {
                const recordIndex = startIndex + visibleIndex;
                return (
                <div
                    key={line.index}
                    id={`agency-jsonl-record-${recordIndex}`}
                    style={{
                        border: '1px solid #e1dfdd',
                        borderRadius: 4,
                        padding: 10,
                        background: recordIndex === shutdownIndex ? '#fff4ce' : '#ffffff',
                    }}
                >
                    <Text variant="small"><strong>Record {recordIndex + 1} (line {line.index + 1})</strong></Text>
                    {line.error && (
                        <MessageBar messageBarType={MessageBarType.warning}>
                            Invalid JSONL record: {line.error}
                        </MessageBar>
                    )}
                    <pre style={preStyle}>
                        {line.value === undefined ? line.raw : JSON.stringify(line.value, null, 2)}
                    </pre>
                </div>
                );
            })}
        </Stack>
    );
}

function parseArtifactIds(taskListId?: string, taskId?: string, workItemId?: string) {
    const parsed = [taskListId, taskId, workItemId].map(value => Number.parseInt(value ?? '', 10));
    if (parsed.some(value => !Number.isInteger(value) || value <= 0)) {
        return undefined;
    }
    return { taskListId: parsed[0], taskId: parsed[1], workItemId: parsed[2] };
}

function formatBytes(value: number) {
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function stripBom(value: string) {
    return value.charCodeAt(0) === 0xfeff ? value.slice(1) : value;
}
